<!--
  File name: UserProfile.vue
  Program Description:
    - This component displays all the fields for viewing and updating the general
      information of a specific user.
    - This also include the functions for deleting and user activation/deactivation
  Includes:
    - ProfileForm
-->
<template>
  <div class="page">
    <v-row
      align-self="start"
      justify="center"
    >
      <v-col
        class="pa-0 pb-5"
      >
        <v-card
          tile
          flat
          color="transparent"
          :loading="$data.$meta.isLoading"
        >
          <v-toolbar
            color="transparent"
            flat
          >
            <v-toolbar-title class="text-h4 font-weight-bold px-0">
              USERS
            </v-toolbar-title>
            <v-spacer />
            <template v-if="operation === 'create'">
              <v-breadcrumbs
                large
                :items="breadcrumbs.create"
                :divider="breadcrumbs.divider"
              />
            </template>
            <template v-else-if="operation === 'reset' ">
              <v-breadcrumbs
                large
                :items="breadcrumbs.resetPass"
                :divider="breadcrumbs.divider"
              />
            </template>
            <template v-else>
              <v-breadcrumbs
                v-if="$data.$meta.readOnly && !$data.$meta.isLoading"
                large
                :items="breadcrumbs.view"
                :divider="breadcrumbs.divider"
              />
              <v-breadcrumbs
                v-else-if="!$data.$meta.isLoading"
                large
                :items="breadcrumbs.update"
                :divider="breadcrumbs.divider"
              />
            </template>
          </v-toolbar>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      v-if="operation !== 'create'"
      no-gutters
      class="container"
    >
      <v-col
        cols="4"
      >
        <v-navigation-drawer
          class="rounded"
          width="100%"
          style="border-right: 1px solid #cdcdcd"
          :style="`background-color: ${$metaData.app.login.background};`"
        >
          <v-list
            class="pa-0"
          >
            <v-list-item-group>
              <DrawerItem
                label="General Information"
                :href="{ name: 'UserProfileView', params: {userRole: 'admin', operation: 'view', id:`${userId}`} }"
                icon="mdi-cog"
              />
              <DrawerItem
                v-if="!isInternal"
                label="Password"
                :href="{ name: 'UserProfileView', params: {userRole: 'admin', operation: 'reset', id:`${userId}`} }"
                icon="mdi-lock"
              />
            </v-list-item-group>
          </v-list>
        </v-navigation-drawer>
      </v-col>
      <v-col
        class="pa-0 ma-auto"
        style="width:100%"
        justify="center"
      >
        <template v-if="operation === 'view' || operation === 'edit'">
          <ProfileForm
            ref="profile"
            :fields="$data"
            :operation="operation"
            :editable="!editable"
            :styles="'py-13 px-15 rounded radius shadow'"
          >
            <template v-slot:action>
              <v-btn
                id="cancel-button"
                color="#162b54"
                text
                class="font-weight-black mr-3"
                @click="cancel "
              >
                Cancel
              </v-btn>
              <v-btn
                v-if="showLabel('canUpdateUserByUserRole')"
                id="update-button"
                style="background-color:#162b54; color: #FFF; "
                text
                type="button"
                :loading="$data.$meta.isLoading"
                @click="confirm('update')"
              >
                {{ getLabel('canUpdateUserByUserRole', 'Update') }}
              </v-btn>
            </template>
          </ProfileForm>
        </template>
        <template v-else-if="operation === 'reset'">
          <v-card
            tile
            class="py-13 px-15 rounded radius shadow"
          >
            <v-card-title class="pa-0">
              <p
                class="font-weight-bold primary--text text-h5 "
              >
                Reset Password
              </p>
              <p
                class="text-subtitle-2"
              >
                <b>
                  It's a good idea to use a strong password that the user are not using elsewhere.
                </b>
              </p>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-container>
                <v-img
                  :src="`${$metaData.app.resetPass}`"
                  width="75%"
                  class="ma-auto"
                />
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-btn
                id="send-link-btn"
                color="primary"
                type="button"
                block
                class="py-7 mt-8 rounded-lg"
                :loading="$data.$meta.isLoading"
                @click="reset"
              >
                Send Password Reset Link
              </v-btn>
            </v-card-actions>
          </v-card>
        </template>
      </v-col>
    </v-row>
    <v-row
      v-if="operation ==='create'"
      no-gutters
      class="container"
    >
      <v-col
        class="pa-0 ma-auto"
        style="width:100%"
        justify="center"
      >
        <ProfileForm
          ref="profile"
          :fields="$data"
          :operation="operation"
          :editable="editable"
          :styles="'formPadding py-13 rounded-lg shadow'"
        >
          <template v-slot:action>
            <v-btn
              id="cancel-button"
              color="#162b54"
              text
              class="font-weight-black mr-3"
              @click="$router.go(-1);"
            >
              Cancel
            </v-btn>
            <v-btn
              v-if="showLabel('canCreateByUserRole') && operation === 'create'"
              id="add-button"
              style="background-color:#162b54; color: #FFF; "
              text
              type="button"
              :loading="$data.$meta.isLoading"
              @click=" confirmCreate();"
            >
              {{ getLabel('canCreateByUserRole', 'Add') }}
            </v-btn>
          </template>
        </ProfileForm>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import users from '@/utils/webapi/users'
import adminProfileMixin from '@/mixins/adminProfileMixin'
import { privilegeMixin } from 'session-plugin'
import ProfileForm from '@/components/local/ProfileForm'
import DrawerItem from '@/components/DrawerItem'
import { validationMixin, custom, validationErrors } from '@/utils/validations'
import { mapGetters } from 'vuex'

export default {
  components: {
    ProfileForm,
    DrawerItem
  },
  mixins: [adminProfileMixin, privilegeMixin, validationMixin],
  data: () => ({
    firstName: '',
    $meta: {
      menu: false,
      rolesIsLoading: false,
      rolesIsDisabled: true
    },
    vuelidateChild: false,
    showFab: false,
    npcrsRegistration: {}
  }),
  validations () {
    if (this.$data.userRole.includes('pic')) {
      return custom.externalUserProfile
    }
    return custom.userProfile
  },
  computed: {
    ...validationErrors,
    ...mapGetters('lookups', {
      profile: 'getCurrentUser'
    }),
    breadcrumbs () {
      return {
        divider: '>',
        large: true,
        create: [
          {
            text: 'Users',
            exact: true,
            to: { name: 'AdminUsersList', params: { userRole: 'admin' } }
          },
          {
            text: 'Add User'
          }
        ],
        update: [
          {
            text: 'Users',
            exact: true,
            to: { name: 'AdminUsersList', params: { userRole: 'admin' } }
          },
          {
            text: `Edit Profile -  ${this.firstName} ${this.lastName}`
          }
        ],
        view: [
          {
            text: 'Users',
            exact: true,
            to: { name: 'AdminUsersList', params: { userRole: 'admin' } }
          },
          {
            text: `View Profile - ${this.firstName} ${this.lastName}`
          }
        ],
        resetPass: [
          {
            text: 'Users',
            exact: true,
            to: { name: 'AdminUsersList', params: { userRole: 'admin' } }
          },
          {
            text: `Reset Password - ${this.firstName} ${this.lastName}`
          }
        ]
      }
    },
    operation () {
      return this.$route.params.operation
    },
    editable () {
      return this.operation === 'create' || this.$data.$meta.readOnly === false
    },
    statusAction () {
      if (this.status === 'Active') return 'deactivate'
      else return 'activate'
    },
    isInternal () {
      return this.$data.userType === 'Internal'
    },
    isAdmin () {
      return (this.userRole.includes('admin'))
    },
    properties () {
      return {
        label: 'Reason',
        name: 'reason',
        value: 'registrar',
        outlined: true,
        'error-messages': this.genericError('reason', 'Reason')
      }
    },
    userDetails () {
      if (this.$data.userType === 'LibraryGuest') return ['libraryGuest']
      return this.userRole
    },
    role () {
      return this.userRole.length > 1 ? 'profile' : this.userRole
    }
  },
  watch: {
    '$route.params.id' () {
      this.getProfile()
    },
    userType (val) {
      this.$emit('input', val)
      this.userType = val
    },
    async email (val) {
      await this.fetchNpcUserData(val)
    }
  },
  async created () {
    if (this.operation === 'create') {
      this.$data.$meta.isLoading = false
      this.$data.$meta.readOnly = false
    } else {
      if (this.operation === 'edit') this.$data.$meta.readOnly = false
      this.userRole = this.$route.params.userRole
      this.getProfile()
    }
  },
  mounted () {
    window.addEventListener('scroll', this.onScroll)
  },
  methods: {
    // get profile details of current user
    async getProfile () {
      return this.getProfileWrap(() => users.getUser(this.role, this.$route.params.id))
    },
    // update profile details with the values from the fields
    async update () {
      const info = this.updateInfo
      info.userRole = this.userDetails
      await this.updateProfileWrap(() => users.patchUser(this.role, this.$route.params.id, info), info)
      this.$store.dispatch('initPrivileges')
      if (this.profile._id === info.userId) {
        info.fullName = `${info.firstName}${' ' + info.middleName || ''} ${info.lastName}`
        info.userRole = this.userDetails.join(', ')
        this.$store.commit('lookups/setCurrentUser', info)
        this.profile._id = info.userId
      }
    },
    // trigger field validations then proceed with confirmation dialog for action when validations pass
    confirm (action) {
      this.isPic ? this.$v.$touch() : this.$refs.profile.$v.$touch()
      const invalid = this.isPic ? this.$v.$invalid : this.$refs.profile.$v.$invalid
      if (!invalid) {
        this.confirmDialog(action)
      } else {
        this.$store.dispatch('showAlert', {
          message: 'Required fields cannot be blank.',
          type: 'error'
        })
      }
    },
    // create user with the details from the fields
    // show dialog with success or error message
    create: async function () {
      this.$data.$meta.readOnly = true
      this.$data.$meta.isLoading = true
      const info = this.updateInfo
      info.userRole = this.userRole
      info.userType = 'Internal'
      const resp = await users.createUser(info)
      if (resp.ok) {
        const jData = await resp.json()
        this.$store.dispatch('showAlert', {
          message: `User ${this.firstName} ${this.lastName} has been successfully added!`,
          type: 'success',
          callback: this.$router.push({
            path: `/admin/users/${this.userRole}/view/${jData.userData._id}`
          })
        })
      } else {
        this.$store.dispatch('showAlert', {
          message: (await resp.json()).error,
          type: 'error'
        })
        this.$data.$meta.readOnly = false
      }
      this.$data.$meta.isLoading = false
    },
    // send reset password link to email of user
    reset: async function () {
      this.$data.$meta.isLoading = true
      const resp = await users.resetPassword(this.email)
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: `A password reset link was successfully sent to ${this.fullName}`,
          type: 'success'
        })
      } else {
        this.$store.dispatch('showAlert', {
          message: (await resp.json()).error,
          type: 'error'
        })
      }
      this.$data.$meta.isLoading = false
    },
    // trigger field validations
    // if valid, show confirmation dialog for action. else, show error
    confirmCreate (action = 'create') {
      this.$refs.profile.$v.$touch()
      if (!this.$refs.profile.$v.$invalid) {
        this.$store.dispatch('showDialog', {
          title: 'Add New User',
          message: 'This will create a new user to DBNMS. Do you wish to proceed?',
          buttonLabel: 'Proceed',
          buttonColor: 'green',
          callback: () => { this[`${action}`]() }
        })
        this.vuelidateChild = !this.vuelidateChild
      } else {
        this.$store.dispatch('showAlert', {
          message: 'Required fields cannot be blank.',
          type: 'error'
        })
      }
    },
    // set readOnly variable to false
    editUser () {
      this.$data.$meta.readOnly = false
    },
    // go to topmost part of page
    scrollToTop () {
      this.$vuetify.goTo(0, 0)
    },
    // show scroll to top fab when a certain scroll down height is reached
    onScroll () {
      this.showFab = window.top.scrollY > 80
    },
    async fetchNpcUserData (email) {
      const resp = await users.verifyDpoEmail(email)
      if (resp.ok) {
        this.npcrsRegistration = await resp.json()
      }
    }
  }
}
</script>

<style scoped>
.page {
  display: grid;
  justify-self: center;
  max-width:1170px;
  margin: auto;
}
.shadow {
    box-shadow:4px 8px 8px -2px #c3c3c3 !important;
}
.radius {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
.container {
  padding: 1% 0%;
}
.v-breadcrumbs >>> a{
  font-weight: bolder;
  color: #000 !important
}
.v-breadcrumbs >>> {
  color: #162b54 !important;
  font-weight: bolder
}

</style>
